import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../../../config/axiosConfig';
import { API } from '../../../../utils/apiRoutes';
import { formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { canAccessSection } from '../../../../config/sectionAccess';
import { useAuth } from '../../../../context/AuthContext';
import { NEW_KEY, sendErrorMessage, useAssistant } from '../../../../context/AssistantContext';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import AssistantActionCard from './AssistantActionCard';
import AssistantMarkdown from './AssistantMarkdown';
import AssistantHistory from './AssistantHistory';
import { ConversationMenu } from '../../../Sidebar/AssistantNav';

const TRANSCRIBE_TIMEOUT_MS = 60000;
// A spoken question is short; this also bounds the upload the server accepts.
const MAX_RECORDING_MS = 60000;
// Below this a recording is treated as silence rather than uploaded.
const MIN_AUDIO_BYTES = 1500;
const NEAR_BOTTOM_PX = 96;

// At most three starters per role, matching the role's server tool set
// (spec 006 §4.3). A click only fills the draft; nothing is sent.
const SUGGESTIONS_BY_ROLE = {
    manager: [
        'درآمد این ماه نسبت به ماه قبل چطور بوده؟',
        'امروز کدام کلاس‌ها حضور و غیاب نخورده؟',
        'کدام هنرجوها بدهکارند؟',
    ],
    secretary: [
        'امروز کدام کلاس‌ها حضور و غیاب نخورده؟',
        'این هفته کدام جلسه‌ها حضور و غیاب ندارند؟',
    ],
    accountant: [
        'درآمد این ماه نسبت به ماه قبل چطور بوده؟',
        'کدام هنرجوها بدهکارند؟',
        'به کدام آموزگار هنوز بدهکاریم؟',
    ],
};
// Roles that may confirm attendance (confirm/cancel are
// IsManagerOrSecretaryWriteOnly on the server, which stays the final check).
const WRITE_ROLES = ['manager', 'secretary'];

// Fixed tool → label/section map. Technical names and arguments never reach
// the normal UI; a section link is shown only when this user may open it.
const TOOL_SOURCES = {
    get_revenue_overview: { label: 'درآمد آموزشگاه', section: 'institution-accounting' },
    get_receivables_summary: { label: 'مطالبات هنرجویان', section: 'student-accounting' },
    get_teacher_settlement_summary: { label: 'تسویهٔ آموزگاران', section: 'teacher-accounting' },
    get_statistics: { label: 'آمار سامانه', section: 'statistics' },
    find_students: { label: 'فهرست هنرجویان', section: 'student-list' },
    who_needs_rollcall: { label: 'حضور و غیاب امروز', section: 'student-card' },
    get_attendance_gaps: { label: 'جلسه‌های بدون حضور و غیاب', section: 'student-card' },
    propose_attendance: { label: 'پیشنهاد ثبت حضور و غیاب', section: null },
};
const UNKNOWN_SOURCE = { label: 'داده‌های آموزشگاه', section: null };

function voiceErrorMessage(err) {
    const name = err?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'اجازهٔ دسترسی به میکروفون داده نشد. آن را در تنظیمات مرورگر فعال کنید.';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'میکروفونی پیدا نشد.';
    if (name === 'NotReadableError') return 'میکروفون در دسترس نیست؛ شاید برنامهٔ دیگری از آن استفاده می‌کند.';
    return 'ضبط صدا شروع نشد.';
}

function Sources({ toolCalls, user }) {
    const [open, setOpen] = useState(false);
    const sources = useMemo(() => {
        const seen = new Map();
        toolCalls
            .filter((call) => call && call.ok !== false)
            .forEach((call) => {
                const meta = TOOL_SOURCES[call.name] || UNKNOWN_SOURCE;
                if (!seen.has(meta.label)) seen.set(meta.label, meta);
            });
        return [...seen.values()];
    }, [toolCalls]);
    if (sources.length === 0) return null;
    return (
        <Box sx={{ mt: 0.75 }}>
            <Button
                size="small"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ px: 0.5, minHeight: 0, fontSize: '0.72rem', color: 'text.secondary' }}
            >
                {toPersianDigits(`منابع این پاسخ (${sources.length})`)}
            </Button>
            <Collapse in={open}>
                <Box component="ul" sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {sources.map((source) => {
                        const linkable = source.section && canAccessSection(user, source.section);
                        return (
                            <Box component="li" key={source.label}>
                                {linkable ? (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        clickable
                                        component={Link}
                                        to={`/dashboard/${source.section}`}
                                        label={`${source.label} ←`}
                                        aria-label={`${source.label}؛ باز کردن صفحهٔ مربوط`}
                                    />
                                ) : (
                                    <Chip size="small" variant="outlined" label={source.label} />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>
        </Box>
    );
}

function Timestamp({ value, align }) {
    if (!value) return null;
    return (
        <Box component="time" dateTime={value} sx={{ typography: 'caption', display: 'block', mt: 0.5, opacity: 0.7, fontSize: '0.68rem', textAlign: align }}>
            {toPersianDigits(formatJalaliDateTime(value))}
        </Box>
    );
}

// Delivery state of a question the user sent from this browser.
function DeliveryState({ item, onRetry, onEdit, onDiscard }) {
    if (!item) return null;
    // The assistant-side "checking data" line is the only waiting indicator.
    if (item.state === 'sending') return null;
    const processing = item.state === 'processing';
    return (
        <Box sx={{ mt: 0.75 }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ErrorOutlineIcon sx={{ fontSize: '0.95rem' }} />
                {item.error || (processing ? 'پاسخ هنوز در حال آماده‌شدن است.' : 'ارسال ناموفق بود.')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" color="inherit" onClick={onRetry} sx={{ color: 'text.primary', minHeight: 0, py: 0.25 }}>
                    {processing ? 'بررسی دوباره' : 'تلاش دوباره'}
                </Button>
                {onEdit && (
                    <Button size="small" color="inherit" onClick={onEdit} sx={{ minHeight: 0, py: 0.25 }}>
                        ویرایش متن
                    </Button>
                )}
                {onDiscard && (
                    <Button size="small" color="inherit" onClick={onDiscard} sx={{ minHeight: 0, py: 0.25 }}>
                        کنار گذاشتن
                    </Button>
                )}
            </Box>
        </Box>
    );
}

function UserBubble({ content, createdAt, children }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    maxWidth: { xs: '88%', sm: '75%' },
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    borderStartStartRadius: 4,
                }}
            >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9, unicodeBidi: 'plaintext', overflowWrap: 'anywhere' }}>
                    {toPersianDigits(content)}
                </Typography>
                {children}
                <Timestamp value={createdAt} align="end" />
            </Box>
        </Box>
    );
}

function AssistantReply({ message, user, actionProps }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                mb: 2,
                px: { xs: 1.25, sm: 2 },
                py: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.5 : 0.7),
                border: `1px solid ${alpha(theme.palette.border.main, 0.35)}`,
            }}
        >
            <AssistantMarkdown text={message.content} />
            {(message.actions || []).map((action) => (
                <AssistantActionCard key={action.id} action={action} {...actionProps} />
            ))}
            <Sources toolCalls={message.tool_calls || []} user={user} />
            <Timestamp value={message.created_at} align="end" />
        </Box>
    );
}

function AssistantChat() {
    const theme = useTheme();
    const { user } = useAuth();
    const assistant = useAssistant();
    const {
        status,
        loadStatus,
        selectedId,
        invalidParam,
        view,
        newChatNonce,
        details,
        loadDetail,
        drafts,
        setDraft,
        pending,
        send,
        retry,
        resume,
        discard,
        openNew,
        requestRename,
        requestDelete,
    } = assistant;

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [showNewIndicator, setShowNewIndicator] = useState(false);
    const [voice, setVoice] = useState({ state: 'idle', seconds: 0 });
    const [voiceHint, setVoiceHint] = useState('');
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const nearBottomRef = useRef(true);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const discardRecordingRef = useRef(false);
    const stopTimerRef = useRef(null);
    const tickRef = useRef(null);
    const voiceTokenRef = useRef(0);

    const statusData = status.data;
    const role = statusData?.role || null;
    const statusReady = Boolean(statusData);
    const aiAvailable = statusData?.ai_available !== false;
    const canWrite = Boolean(statusData?.actions_enabled) && WRITE_ROLES.includes(role);
    const suggestions = (role && SUGGESTIONS_BY_ROLE[role]) || [];
    const serverOffsetMs = useMemo(() => {
        const serverNow = statusData?.server_time ? Date.parse(statusData.server_time) : NaN;
        return Number.isNaN(serverNow) ? 0 : serverNow - Date.now();
    }, [statusData?.server_time]);

    const key = selectedId ?? `new:${newChatNonce}`;
    const draftKey = selectedId ?? NEW_KEY;
    const draft = drafts[draftKey] || '';
    const entry = selectedId ? details[selectedId] : null;
    // Never render another thread's data while the selected one loads.
    const conversation = entry?.data && entry.data.id === selectedId ? entry.data : null;
    const pendingItems = useMemo(() => pending[key] || [], [pending, key]);
    const sending = pendingItems.some((item) => item.state === 'sending');

    const showMessage = useCallback((message, severity = 'error') => setSnackbar({ open: true, message, severity }), []);
    const focusInput = useCallback(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    // Open (or reopen) a thread: show the cached copy at once, refresh quietly.
    const detailsRef = useRef(details);
    detailsRef.current = details;
    useEffect(() => {
        if (!selectedId) return;
        loadDetail(selectedId, { silent: Boolean(detailsRef.current[selectedId]?.data) });
    }, [selectedId, loadDetail]);

    // Page title follows the thread.
    useEffect(() => {
        const previous = document.title;
        const label = view === 'history' ? 'همهٔ گفتگوها' : conversation?.title || 'گفتگوی جدید';
        document.title = `${label} · دستیار هوشمند · برنا`;
        return () => {
            document.title = previous;
        };
    }, [view, conversation?.title]);

    // --- rendered thread ---------------------------------------------------
    const pendingByClient = useMemo(
        () => Object.fromEntries(pendingItems.map((item) => [item.clientId, item])),
        [pendingItems],
    );
    const serverMessages = useMemo(() => conversation?.messages || [], [conversation]);
    const serverClientIds = useMemo(
        () => new Set(serverMessages.map((m) => m.client_request_id).filter(Boolean)),
        [serverMessages],
    );
    const unsentItems = pendingItems.filter((item) => !serverClientIds.has(item.clientId));
    const lastServer = serverMessages[serverMessages.length - 1];
    const orphan =
        lastServer && lastServer.role === 'user' && lastServer.client_request_id && !pendingByClient[lastServer.client_request_id]
            ? lastServer
            : null;
    const itemCount = serverMessages.length + unsentItems.length;

    // --- scrolling: follow new messages only when already near the end ----
    const scrollToBottom = useCallback((smooth = false) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        nearBottomRef.current = true;
        setShowNewIndicator(false);
    }, []);
    useLayoutEffect(() => {
        scrollToBottom(false);
    }, [key, conversation?.id, scrollToBottom]);
    const prevCountRef = useRef(itemCount);
    useEffect(() => {
        const grew = itemCount > prevCountRef.current;
        prevCountRef.current = itemCount;
        if (!grew) return;
        if (nearBottomRef.current) scrollToBottom(true);
        else setShowNewIndicator(true);
    }, [itemCount, scrollToBottom]);
    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
        if (nearBottomRef.current) setShowNewIndicator(false);
    };

    // --- sending -----------------------------------------------------------
    const canCompose = statusReady && aiAvailable && !invalidParam && (!selectedId || Boolean(conversation));
    const submit = () => {
        if (!canCompose || sending || voice.state !== 'idle') return;
        nearBottomRef.current = true; // your own message always scrolls into view
        if (send(key, draft)) {
            setDraft(draftKey, '');
            setVoiceHint('');
        }
    };

    // --- voice: record -> transcribe -> editable draft (never auto-sent) ---
    const clearVoiceTimers = () => {
        clearTimeout(stopTimerRef.current);
        clearInterval(tickRef.current);
        stopTimerRef.current = null;
        tickRef.current = null;
    };
    const releaseStream = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };
    const cancelRecording = useCallback(() => {
        discardRecordingRef.current = true;
        voiceTokenRef.current += 1; // a late transcript must not land anywhere
        clearVoiceTimers();
        if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
        recorderRef.current = null;
        releaseStream();
        setVoice({ state: 'idle', seconds: 0 });
    }, []);
    const finishRecording = () => {
        discardRecordingRef.current = false;
        clearVoiceTimers();
        if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    };

    const transcribe = async (blob, token, targetKey) => {
        setVoice({ state: 'transcribing', seconds: 0 });
        try {
            const extension = { 'audio/mp4': 'mp4', 'audio/ogg': 'ogg' }[(blob.type || '').split(';')[0]] || 'webm';
            const form = new FormData();
            form.append('audio', blob, `question.${extension}`);
            const response = await api.post(API.assistantTranscribe, form, { timeout: TRANSCRIBE_TIMEOUT_MS });
            if (token !== voiceTokenRef.current) return;
            // The Persian, name-corrected text is what the user reviews and
            // sends; `transcript` is the older field kept for compatibility.
            const text = (response.data?.corrected_transcript || response.data?.transcript || '').trim();
            if (!text) {
                showMessage('چیزی شنیده نشد. دوباره تلاش کنید.', 'warning');
                return;
            }
            const current = (assistantRef.current.drafts[targetKey] || '').trim();
            setDraft(targetKey, current ? `${current} ${text}` : text);
            setVoiceHint('متن گفته‌شده در کادر پیام قرار گرفت؛ نام‌ها و تاریخ‌ها را بررسی و در صورت نیاز اصلاح کنید، سپس ارسال کنید.');
            focusInput();
        } catch (err) {
            if (token !== voiceTokenRef.current) return;
            showMessage(sendErrorMessage(err, 'صدای شما تبدیل به متن نشد.'));
        } finally {
            if (token === voiceTokenRef.current) setVoice({ state: 'idle', seconds: 0 });
        }
    };
    const assistantRef = useRef(assistant);
    assistantRef.current = assistant;

    const startRecording = async () => {
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            showMessage('مرورگر شما ضبط صدا را پشتیبانی نمی‌کند.');
            return;
        }
        const token = ++voiceTokenRef.current;
        const targetKey = draftKey;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (token !== voiceTokenRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            streamRef.current = stream;
            // Safari leaves recorder.mimeType empty unless a type is requested.
            // Opus at 32 kbps keeps uploads small; speech needs no more.
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((type) => MediaRecorder.isTypeSupported?.(type));
            const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32000 });
            const chunks = [];
            discardRecordingRef.current = false;
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunks.push(event.data);
            };
            recorder.onstop = () => {
                releaseStream();
                if (discardRecordingRef.current || token !== voiceTokenRef.current) return;
                const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
                if (blob.size < MIN_AUDIO_BYTES) {
                    setVoice({ state: 'idle', seconds: 0 });
                    showMessage('صدایی ضبط نشد. دوباره تلاش کنید.', 'warning');
                    return;
                }
                transcribe(blob, token, targetKey);
            };
            recorderRef.current = recorder;
            recorder.start();
            setVoice({ state: 'recording', seconds: 0 });
            setVoiceHint('');
            tickRef.current = setInterval(() => setVoice((v) => (v.state === 'recording' ? { ...v, seconds: v.seconds + 1 } : v)), 1000);
            stopTimerRef.current = setTimeout(finishRecording, MAX_RECORDING_MS);
        } catch (err) {
            releaseStream();
            showMessage(voiceErrorMessage(err));
        }
    };

    // Leaving the page or switching threads discards a recording in progress
    // (no upload, no transcript) and releases the microphone.
    useEffect(() => cancelRecording, [key, cancelRecording]);

    // --- states ---------------------------------------------------------------
    if (status.httpStatus === 403) {
        return <Alert severity="warning">دستیار هوشمند برای نقش شما در دسترس نیست.</Alert>;
    }

    const titleText = view === 'history' ? 'همهٔ گفتگوها' : conversation?.title || (selectedId ? '' : 'گفتگوی جدید');
    const helperText = canWrite
        ? 'دستیار فقط داده‌های همین آموزشگاه را می‌خواند و بدون تأیید شما چیزی ثبت نمی‌شود.'
        : 'دستیار فقط داده‌های همین آموزشگاه را می‌خواند و چیزی را تغییر نمی‌دهد.';

    let body;
    if (view === 'history') {
        body = null;
    } else if (invalidParam) {
        body = (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={openNew}>گفتگوی جدید</Button>}>
                نشانی این گفتگو معتبر نیست.
            </Alert>
        );
    } else if (selectedId && !conversation) {
        if (entry?.httpStatus === 404 || entry?.httpStatus === 403) {
            body = (
                <Alert severity="info" action={<Button color="inherit" size="small" onClick={openNew}>گفتگوی جدید</Button>}>
                    این گفتگو پیدا نشد؛ ممکن است حذف شده باشد یا متعلق به حساب دیگری باشد.
                </Alert>
            );
        } else if (entry?.error) {
            body = (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => loadDetail(selectedId)}>تلاش دوباره</Button>}>
                    {entry.error}
                </Alert>
            );
        } else {
            body = (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={26} aria-label="در حال بارگذاری گفتگو" />
                </Box>
            );
        }
    } else if (!selectedId && unsentItems.length === 0) {
        body = (
            <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 3, sm: 8 }, textAlign: 'center', '&& .MuiTypography-root': { textAlign: 'center' } }}>
                <AutoAwesomeOutlinedIcon color="primary" />
                <Typography component="h2" sx={{ fontWeight: 700, mt: 1, fontSize: '1.05rem' }}>
                    از آموزشگاه خود بپرسید
                </Typography>
                {status.loading ? (
                    <CircularProgress size={20} sx={{ mt: 2 }} aria-label="در حال آماده‌سازی دستیار" />
                ) : statusReady ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                            {helperText}
                        </Typography>
                        {aiAvailable && suggestions.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                {suggestions.map((suggestion) => (
                                    <Chip
                                        key={suggestion}
                                        label={suggestion}
                                        variant="outlined"
                                        onClick={() => {
                                            setDraft(NEW_KEY, suggestion);
                                            focusInput();
                                        }}
                                        sx={{ fontSize: '0.78rem', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                                    />
                                ))}
                            </Box>
                        )}
                    </>
                ) : null}
            </Box>
        );
    } else {
        body = (
            <>
                {serverMessages.map((message) => {
                    if (message.role !== 'user') {
                        return (
                            <AssistantReply
                                key={message.id}
                                message={message}
                                user={user}
                                actionProps={{
                                    serverOffsetMs,
                                    canAct: canWrite,
                                    onChanged: () => selectedId && loadDetail(selectedId, { silent: true }),
                                    onError: showMessage,
                                }}
                            />
                        );
                    }
                    const item = message.client_request_id ? pendingByClient[message.client_request_id] : null;
                    return (
                        <UserBubble key={message.id} content={message.content} createdAt={message.created_at}>
                            <DeliveryState
                                item={item}
                                onRetry={() => retry(key, item.clientId)}
                                onDiscard={item && item.state !== 'sending' ? () => discard(key, item.clientId) : null}
                            />
                            {orphan && orphan.id === message.id && aiAvailable && (
                                <Box sx={{ mt: 0.75 }}>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        این پرسش هنوز پاسخی نگرفته است.
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="inherit"
                                        sx={{ color: 'text.primary', mt: 0.5, minHeight: 0, py: 0.25 }}
                                        onClick={() => resume(key, message.client_request_id, message.content)}
                                    >
                                        دریافت پاسخ
                                    </Button>
                                </Box>
                            )}
                        </UserBubble>
                    );
                })}
                {unsentItems.map((item) => (
                    <UserBubble key={item.clientId} content={item.content}>
                        <DeliveryState
                            item={item}
                            onRetry={() => retry(key, item.clientId)}
                            onEdit={
                                item.state === 'failed'
                                    ? () => {
                                          discard(key, item.clientId);
                                          setDraft(draftKey, item.content);
                                          focusInput();
                                      }
                                    : null
                            }
                            onDiscard={item.state !== 'sending' ? () => discard(key, item.clientId) : null}
                        />
                    </UserBubble>
                ))}
                {sending && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                        <CircularProgress size={14} color="inherit" />
                        <Typography variant="caption">در حال بررسی داده‌های آموزشگاه…</Typography>
                    </Box>
                )}
            </>
        );
    }

    const recording = voice.state === 'recording';
    const transcribing = voice.state === 'transcribing';

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                border: { xs: 'none', sm: `1px solid ${alpha(theme.palette.border.main, 0.35)}` },
                borderRadius: { xs: 0, sm: 2 },
                overflow: 'hidden',
                // The theme centres every Typography; chat text reads from the start edge.
                '& .MuiTypography-root': { textAlign: 'start' },
            }}
        >
            {view === 'history' ? (
                <AssistantHistory />
            ) : (
                <>
                    {/* Title bar: this thread's title and its controls only. */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: { xs: 1.5, sm: 2 },
                            minHeight: 48,
                            borderBottom: `1px solid ${alpha(theme.palette.border.main, 0.35)}`,
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            component="h1"
                            noWrap
                            title={titleText}
                            sx={{ flex: 1, minWidth: 0, m: 0, fontWeight: 700, fontSize: '0.95rem' }}
                        >
                            {toPersianDigits(titleText)}
                        </Typography>
                        {conversation && (
                            <IconButton
                                size="small"
                                aria-label="گزینه‌های این گفتگو"
                                aria-haspopup="menu"
                                onClick={(event) => setMenuAnchor(event.currentTarget)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        )}
                        {selectedId && (
                            <Button size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ flexShrink: 0 }}>
                                گفتگوی جدید
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                        <Box
                            ref={scrollRef}
                            onScroll={onScroll}
                            role="log"
                            aria-label="پیام‌های گفتگو"
                            aria-busy={sending}
                            sx={{ height: '100%', overflowY: 'auto', overscrollBehavior: 'contain', px: { xs: 1.5, sm: 3 }, py: 2 }}
                        >
                            <Box sx={{ maxWidth: 960, mx: 'auto' }}>
                                {status.error && status.httpStatus !== 403 && (
                                    <Alert
                                        severity="error"
                                        sx={{ mb: 2 }}
                                        action={<Button color="inherit" size="small" onClick={loadStatus}>تلاش دوباره</Button>}
                                    >
                                        {status.error}
                                    </Alert>
                                )}
                                {body}
                            </Box>
                        </Box>
                        {showNewIndicator && (
                            <Chip
                                color="primary"
                                icon={<ArrowDownwardIcon />}
                                label="پیام جدید"
                                onClick={() => scrollToBottom(true)}
                                sx={{ position: 'absolute', bottom: 12, insetInline: 0, mx: 'auto', width: 'fit-content', boxShadow: 2 }}
                            />
                        )}
                    </Box>

                    {/* Composer */}
                    <Box
                        component="form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            submit();
                        }}
                        sx={{
                            flexShrink: 0,
                            borderTop: `1px solid ${alpha(theme.palette.border.main, 0.35)}`,
                            px: { xs: 1, sm: 2 },
                            pt: 1,
                            pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                        }}
                    >
                        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
                            {statusReady && !aiAvailable && (
                                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                                    تولید پاسخ در حال حاضر فعال نیست؛ گفتگوهای قبلی همچنان قابل مشاهده‌اند.
                                </Alert>
                            )}
                            {voiceHint && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }} role="status">
                                    {voiceHint}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                {statusData?.voice_available && aiAvailable && (
                                    <>
                                        <Tooltip title={recording ? 'پایان ضبط و تبدیل به متن' : 'پرسش صوتی'}>
                                            <span>
                                                <IconButton
                                                    color={recording ? 'error' : 'default'}
                                                    disabled={!canCompose || transcribing}
                                                    onClick={recording ? finishRecording : startRecording}
                                                    aria-label={recording ? 'پایان ضبط و تبدیل به متن' : 'ضبط پرسش صوتی'}
                                                >
                                                    {transcribing ? (
                                                        <CircularProgress size={20} />
                                                    ) : recording ? (
                                                        <StopCircleOutlinedIcon />
                                                    ) : (
                                                        <MicNoneOutlinedIcon />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {recording && (
                                            <Tooltip title="لغو ضبط">
                                                <IconButton onClick={cancelRecording} aria-label="لغو ضبط بدون تبدیل">
                                                    <CloseIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                                <TextField
                                    inputRef={inputRef}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={1}
                                    maxRows={6}
                                    placeholder={
                                        recording
                                            ? toPersianDigits(`در حال ضبط… ${voice.seconds} ثانیه`)
                                            : transcribing
                                              ? 'در حال تبدیل صدا به متن…'
                                              : 'پرسش خود را بنویسید…'
                                    }
                                    value={draft}
                                    disabled={!canCompose || recording || transcribing}
                                    onChange={(event) => setDraft(draftKey, event.target.value)}
                                    onKeyDown={(event) => {
                                        // Enter sends, Shift+Enter adds a line; never mid-IME composition.
                                        if (
                                            event.key === 'Enter' &&
                                            !event.shiftKey &&
                                            !event.nativeEvent.isComposing &&
                                            event.keyCode !== 229
                                        ) {
                                            event.preventDefault();
                                            submit();
                                        }
                                    }}
                                    inputProps={{ 'aria-label': 'متن پرسش', 'aria-describedby': 'assistant-composer-help' }}
                                    // Global form margins would push the field off the buttons' line.
                                    sx={{ m: 0 }}
                                />
                                <IconButton
                                    type="submit"
                                    color="primary"
                                    disabled={!canCompose || sending || !draft.trim() || recording || transcribing}
                                    aria-label="ارسال پرسش"
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        flexShrink: 0,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: 'primary.dark' },
                                        '&.Mui-disabled': { bgcolor: alpha(theme.palette.primary.main, 0.25) },
                                    }}
                                >
                                    <SendIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
                                </IconButton>
                            </Box>
                            <Typography id="assistant-composer-help" variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.5, fontSize: '0.68rem' }}>
                                {`Enter برای ارسال، Shift+Enter برای خط جدید. ${canWrite ? 'بدون تأیید شما چیزی ثبت نمی‌شود.' : 'دستیار فقط می‌خواند.'}`}
                            </Typography>
                            <Box
                                aria-live="polite"
                                sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
                            >
                                {sending ? 'پرسش ارسال شد؛ در انتظار پاسخ دستیار.' : ''}
                            </Box>
                        </Box>
                    </Box>
                </>
            )}

            <ConversationMenu
                anchorEl={menuAnchor}
                conversation={conversation}
                onClose={() => setMenuAnchor(null)}
                onRename={requestRename}
                onDelete={requestDelete}
            />

            <FloatingSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
}

export default AssistantChat;
